import { Button, FileButton, Group, Image, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconPhoto, IconX } from '@tabler/icons-react';
import {
  FEEDBACK_SCREENSHOT_MIME_TYPES,
  MAX_FEEDBACK_CHARS,
  MAX_FEEDBACK_SCREENSHOT_BYTES,
} from '@unify/fhir-model/schemas/product-feedback.schema';
import { useEffect, useRef, useState } from 'react';

const MAX_SCREENSHOT_MB = MAX_FEEDBACK_SCREENSHOT_BYTES / (1024 * 1024);
const SCREENSHOT_ACCEPT = FEEDBACK_SCREENSHOT_MIME_TYPES.join(',');

export interface FeedbackModalProps {
  opened: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: { feedback: string; screenshot: File | null }) => void;
}

export function FeedbackModal({
  opened,
  submitting,
  onClose,
  onSubmit,
}: FeedbackModalProps): JSX.Element {
  const [feedback, setFeedback] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const resetScreenshotRef = useRef<() => void>(null);

  useEffect(() => {
    if (!opened) {
      setFeedback('');
      setScreenshot(null);
      setScreenshotError(null);
    }
  }, [opened]);

  useEffect(() => {
    if (!screenshot) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(screenshot);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  const handleScreenshot = (file: File | null): void => {
    if (!file) {
      return;
    }
    const mimeType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
    if (!(FEEDBACK_SCREENSHOT_MIME_TYPES as readonly string[]).includes(mimeType)) {
      setScreenshotError('Please attach an image file (PNG, JPG, WebP, or GIF).');
      return;
    }
    if (file.size > MAX_FEEDBACK_SCREENSHOT_BYTES) {
      setScreenshotError(`Screenshot must be ${MAX_SCREENSHOT_MB} MB or smaller.`);
      return;
    }
    setScreenshotError(null);
    setScreenshot(file);
  };

  const clearScreenshot = (): void => {
    setScreenshot(null);
    setScreenshotError(null);
    resetScreenshotRef.current?.();
  };

  const canSubmit = Boolean(feedback.trim() && !submitting);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Submit feedback"
      size="lg"
      centered
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
      withCloseButton={!submitting}
    >
      <Stack gap="md">
        <Textarea
          label="Feedback"
          placeholder="Tell us what's working, what's not, or what you'd like to see."
          autosize
          minRows={10}
          maxRows={24}
          value={feedback}
          onChange={(event) => setFeedback(event.currentTarget.value)}
          maxLength={MAX_FEEDBACK_CHARS}
          disabled={submitting}
        />
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Screenshot (optional)
          </Text>
          {previewUrl && screenshot ? (
            <Stack gap="xs">
              <Image
                src={previewUrl}
                alt=""
                maw="100%"
                mah={220}
                fit="contain"
                bd="1px solid var(--mantine-color-gray-4)"
              />
              <Group justify="space-between">
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {screenshot.name}
                </Text>
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-sm"
                  leftSection={<IconX size={14} />}
                  onClick={clearScreenshot}
                  disabled={submitting}
                >
                  Remove
                </Button>
              </Group>
            </Stack>
          ) : (
            <FileButton
              resetRef={resetScreenshotRef}
              onChange={handleScreenshot}
              accept={SCREENSHOT_ACCEPT}
              disabled={submitting}
            >
              {(props) => (
                <Button variant="default" leftSection={<IconPhoto size={16} />} disabled={submitting} {...props}>
                  Attach screenshot
                </Button>
              )}
            </FileButton>
          )}
          <Text size="xs" c="dimmed">
            PNG, JPG, WebP, or GIF up to {MAX_SCREENSHOT_MB} MB. You can submit without one.
          </Text>
          {screenshotError && (
            <Text size="sm" c="red.6">
              {screenshotError}
            </Text>
          )}
        </Stack>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            loading={submitting}
            disabled={!canSubmit}
            onClick={() => onSubmit({ feedback: feedback.trim(), screenshot })}
          >
            Submit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
